import { Query, Question } from '@jupiterone/jupiter-types/question';
import {
  useFormatQueryUrl,
  MockHook,
  useABAC,
} from '@jupiterone/jupiter-web-toolkit';
import { useEffect, useState } from 'react';
import { useAnchorElState, useBooleanState } from 'react-use-object-state';
import { useFeatureFlags } from '@jupiterone/jupiter-web-toolkit/src/components/util/FeatureFlagsContext';
import { questionHasTemplates } from '@jupiterone/jupiter-web-toolkit/src/components/util/queryTemplate';

export interface QuestionResultProps {
  question: Question;
  onRemoveResult: VoidFunction;
  onUpdateResult: (question: Question) => void;
  className: string;
}

const useQuestionResultLogic = ({
  question,
  onRemoveResult,
  onUpdateResult,
  className,
}: QuestionResultProps) => {
  const { formatQueryUrl } = useFormatQueryUrl();
  const [selectedRowID, setSelectedRowID] = useState<null | string>(null);
  const { hasFeature } = useFeatureFlags();
  const { isAdmin } = useABAC();
  const hasValidTrendData = useBooleanState(false);
  const questionModalOpen = useBooleanState(false);
  const shareModalOpen = useBooleanState(false);
  const hasTemplates = useBooleanState(false);
  const [questionUrl, setQuestionUrl] = useState<string>('');
  const [templatedQueries, setTemplatedQueries] = useState<Query[]>(
    [] as Query[]
  );
  const templateModalOpen = useBooleanState(false);
  const addToInsightsMenu = useAnchorElState(null);

  useEffect(() => {
    setTemplatedQueries([]);
    if (questionHasTemplates(question)) {
      hasTemplates.setTrue();
      templateModalOpen.setTrue();
    } else {
      hasTemplates.setFalse();
    }
  }, [question]);

  useEffect(() => {
    if (templatedQueries.length) {
      templateModalOpen.setFalse();
    }
  }, [templatedQueries]);

  const openShareDialog = async () => {
    await formatQueryUrl({ questionId: question.id })
      .then(setQuestionUrl)
      .then(shareModalOpen.toggle);
  };
  return {
    hasValidTrendData: hasValidTrendData.state,
    isGlobalAdmin: isAdmin,
    insightsMenu: addToInsightsMenu.anchorEl,
    questionModalOpen: questionModalOpen.state,
    hasTemplates: hasTemplates.state,
    templatedQueries,
    templateModalOpen: templateModalOpen.state,
    shareModalOpen: shareModalOpen.state,
    questionUrl,
    selectedRowID,
    setValidTrendData: hasValidTrendData.setState,
    setInsightsMenu: addToInsightsMenu.setAnchorEl,
    clearInsightsMenu: addToInsightsMenu.clearAnchorEl,
    questionModalToggle: questionModalOpen.toggle,
    questionModalClose: questionModalOpen.setFalse,
    hasFeature,
    templateModalClose: templateModalOpen.setFalse,
    shareModalClose: shareModalOpen.setFalse,
    setTemplatedQueries,
    openShareDialog,
    setSelectedRowID,
    question,
    onRemoveResult,
    onUpdateResult,
    className,
  };
};

export type QuestionResultComponentProps = ReturnType<
  typeof useQuestionResultLogic
>;

export const MOCK_QUESTION_RESULT_PROPS: QuestionResultComponentProps = {
  className: '',
  hasValidTrendData: false,
  isGlobalAdmin: false,
  insightsMenu: null,
  questionModalOpen: false,
  hasTemplates: true,
  templatedQueries: [],
  templateModalOpen: false,
  shareModalOpen: false,
  questionUrl: '',
  setValidTrendData: MockHook.mockFunction,
  setInsightsMenu: MockHook.mockFunction,
  clearInsightsMenu: MockHook.mockFunction,
  questionModalToggle: MockHook.mockFunction,
  questionModalClose: MockHook.mockFunction,
  hasFeature: MockHook.mockFunction,
  templateModalClose: MockHook.mockFunction,
  shareModalClose: MockHook.mockFunction,
  setTemplatedQueries: MockHook.mockFunction,
  openShareDialog: MockHook.mockFunction,
  onRemoveResult: MockHook.mockFunction,
  onUpdateResult: MockHook.mockFunction,
  setSelectedRowID: MockHook.mockFunction,
  selectedRowID: null,
  question: {
    id: '',
    title: 'question logic',
    showTrend: false,
    queries: [],
    name: 'question',
    lastUpdatedTimestamp: 1000000,
  },
};

export default MockHook.useMockHook(
  useQuestionResultLogic,
  MOCK_QUESTION_RESULT_PROPS
);
